import { useEffect, useRef, useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { isStandalone } from "../pwa/platform";
import { applyUpdate } from "./applyUpdate";
import { useAppUpdateStore } from "./appUpdateStore";
import { fetchDeployedBuildId, readCurrentBuildId } from "./buildId";
import { hasUnsavedChanges } from "./unsavedChanges";

/** `REACT_APP_UPDATE_POLL_MS` is a testing knob (read at build time); production uses the default. */
const POLL_INTERVAL_MS = Number(process.env.REACT_APP_UPDATE_POLL_MS) || 10 * 60_000;
/** Floor between checks, so returning to a tab repeatedly cannot turn into a request loop. */
const MIN_CHECK_GAP_MS = 60_000;
/**
 * An installed app is resumed, not restarted — the old document can live for days. Coming back
 * after at least this long is the moment to land on the new build. Shorter absences (a photo
 * picker, a phone call) must not reload.
 */
const RESUME_RELOAD_AFTER_MS = 2 * 60_000;

export type AppUpdateState = {
  updateAvailable: boolean;
};

/**
 * Notice a deploy and reload into it on the next navigation — the screen the user was on is
 * already gone at that point, so the reload destroys nothing. Someone who never navigates keeps
 * the old build until they do.
 *
 * `pathname` is a parameter (not `useLocation()`) so `shared` stays free of React Router;
 * the app-layer `AppUpdater` supplies it.
 *
 * An installed (standalone) app additionally reloads when it is brought back to the foreground
 * after `RESUME_RELOAD_AFTER_MS`, unless a save is in flight or a form holds unsaved input.
 * `updateAvailable` lives in `useAppUpdateStore` so the banner can offer the reload by hand.
 */
export function useAppUpdate(pathname: string): AppUpdateState {
  // Read once: the document's bundle cannot change while the document is alive.
  const [currentBuildId] = useState(readCurrentBuildId);
  const updateAvailable = useAppUpdateStore((s) => s.updateAvailable);
  const setUpdateAvailable = useAppUpdateStore((s) => s.setUpdateAvailable);
  const [pathnameAtDetection, setPathnameAtDetection] = useState<string | null>(null);

  const mutationsInFlight = useIsMutating();
  const mutationsRef = useRef(mutationsInFlight);
  mutationsRef.current = mutationsInFlight;

  // No check on mount — the document just loaded, so it *is* the deployed build.
  useEffect(() => {
    if (!currentBuildId || updateAvailable) return;

    let cancelled = false;
    let lastCheckedAt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();

    const check = async () => {
      if (cancelled || document.hidden || !navigator.onLine) return;
      if (Date.now() - lastCheckedAt < MIN_CHECK_GAP_MS) return;
      lastCheckedAt = Date.now();

      try {
        const deployedBuildId = await fetchDeployedBuildId(controller.signal);
        // `null` is "no answer yet", not "no update".
        if (cancelled || !deployedBuildId) return;
        if (deployedBuildId !== currentBuildId) setUpdateAvailable(true);
      } catch {
        // Offline or aborted — the poll retries by itself.
      }
    };

    const tick = () => {
      void check();
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    const checkOnWake = () => {
      if (!document.hidden) void check();
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", checkOnWake);
    window.addEventListener("focus", checkOnWake);

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== undefined) clearTimeout(timer);
      document.removeEventListener("visibilitychange", checkOnWake);
      window.removeEventListener("focus", checkOnWake);
    };
  }, [currentBuildId, updateAvailable, setUpdateAvailable]);

  // Resume reload, installed app only. Ignores the check floor on purpose: the user is about to
  // look at the screen, so a fresh answer beats a cached "no".
  useEffect(() => {
    if (!currentBuildId) return;

    let hiddenAt: number | null = null;
    let cancelled = false;
    const controller = new AbortController();

    const onVisibility = async () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        return;
      }
      const wasHiddenFor = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      if (wasHiddenFor < RESUME_RELOAD_AFTER_MS || !isStandalone() || !navigator.onLine) return;

      let found = useAppUpdateStore.getState().updateAvailable;
      if (!found) {
        try {
          const deployedBuildId = await fetchDeployedBuildId(controller.signal);
          found = !!deployedBuildId && deployedBuildId !== currentBuildId;
        } catch {
          return;
        }
        if (cancelled || !found) return;
        setUpdateAvailable(true);
      }
      if (mutationsRef.current > 0 || hasUnsavedChanges()) return;
      applyUpdate();
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      controller.abort();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [currentBuildId, setUpdateAvailable]);

  // Leaving the screen the user was on when the update turned up is the signal to reload.
  useEffect(() => {
    if (!updateAvailable) return;
    setPathnameAtDetection((current) => current ?? pathname);
  }, [updateAvailable, pathname]);

  // The mutation gate covers navigate-while-saving: a reload mid-request could abort a save the
  // user believes has happened.
  useEffect(() => {
    if (!updateAvailable || mutationsInFlight > 0) return;
    if (pathnameAtDetection === null || pathname === pathnameAtDetection) return;
    applyUpdate();
  }, [updateAvailable, mutationsInFlight, pathname, pathnameAtDetection]);

  return { updateAvailable };
}
